import { getMailApiBase } from "@/auth/paths";
import { mailAuthHeaders, readStoredSuiteUserId } from "@/auth/session";
import { MAIL_FOLDERS_FETCH_TIMEOUT_MS } from "@/lib/boot/mailBootConstants";
import { withImapExclusiveOp } from "@/lib/mail/imapClientLock";
import { mailApiGet, mailApiPost } from "./client";
import type {
  InboxIncrementalResponse,
  InboxMessagesResponse,
  MailFoldersResponse,
  MailMessageResponse,
} from "../types/mail";

export function fetchInboxMessages(params?: {
  accountId?: number;
  folder?: string;
  limit?: number;
  offset?: number;
  refresh?: boolean;
  q?: string;
  unseen?: boolean;
}) {
  const qs = new URLSearchParams();
  if (params?.accountId) qs.set("accountId", String(params.accountId));
  if (params?.folder) qs.set("folder", params.folder);
  if (params?.limit) qs.set("limit", String(params.limit));
  if (params?.offset) qs.set("offset", String(params.offset));
  if (params?.refresh) qs.set("refresh", "1");
  if (params?.q?.trim()) qs.set("q", params.q.trim());
  if (params?.unseen) qs.set("unseen", "1");

  const query = qs.toString();
  const path = query ? `imap/messages.php?${query}` : "imap/messages.php";

  return mailApiGet<InboxMessagesResponse>(path);
}

export function fetchInboxIncremental(params?: {
  accountId?: number;
  folder?: string;
  limit?: number;
}) {
  const qs = new URLSearchParams();
  if (params?.accountId) qs.set("accountId", String(params.accountId));
  if (params?.folder) qs.set("folder", params.folder);
  if (params?.limit) qs.set("limit", String(params.limit));

  const query = qs.toString();
  const path = query
    ? `imap/sync-incremental.php?${query}`
    : "imap/sync-incremental.php";

  return mailApiGet<InboxIncrementalResponse>(path);
}

export type BodyCacheStatusResponse = {
  success: boolean;
  accountId: number;
  folder: string;
  flags: Record<string, boolean>;
};

export function fetchBodyCacheStatus(params: {
  accountId: number;
  folder: string;
  uids: number[];
}) {
  if (params.uids.length === 0) {
    return Promise.resolve({
      ok: true as const,
      data: {
        success: true,
        accountId: params.accountId,
        folder: params.folder,
        flags: {},
      },
    });
  }

  const qs = new URLSearchParams();
  qs.set("accountId", String(params.accountId));
  qs.set("folder", params.folder);
  qs.set("uids", params.uids.join(","));

  return mailApiGet<BodyCacheStatusResponse>(`imap/bodies-status.php?${qs.toString()}`);
}

export type BodiesBootstrapResponse = {
  success: boolean;
  accountId: number;
  folder: string;
  messages: import("../types/mail").MailMessageDetail[];
  missing: number[];
};

/** Lecture batch DD02 (.eml) — pas d’IMAP. Cap serveur ~40 UID / requête. */
export function fetchBodiesBootstrap(
  params: {
    accountId: number;
    folder: string;
    uids: number[];
  },
  options?: { timeoutMs?: number }
) {
  if (params.uids.length === 0) {
    return Promise.resolve({
      ok: true as const,
      data: {
        success: true,
        accountId: params.accountId,
        folder: params.folder,
        messages: [],
        missing: [],
      } satisfies BodiesBootstrapResponse,
    });
  }

  return mailApiPost<BodiesBootstrapResponse>(
    "imap/bodies-bootstrap.php",
    {
      accountId: params.accountId,
      folder: params.folder,
      uids: params.uids,
    },
    { timeoutMs: options?.timeoutMs ?? 25_000 }
  );
}

export type WarmBodiesResponse = {
  success: boolean;
  accountId: number;
  folder: string;
  warmed: number;
  /** true = job mis en queue worker (pas d’IMAP dans Apache). */
  queued?: boolean;
  flags: Record<string, boolean>;
};

export function warmMessageBodies(params: {
  accountId: number;
  folder: string;
  uids: number[];
}) {
  if (params.uids.length === 0) {
    return Promise.resolve({
      ok: true as const,
      data: {
        success: true,
        accountId: params.accountId,
        folder: params.folder,
        warmed: 0,
        queued: false,
        flags: {},
      } satisfies WarmBodiesResponse,
    });
  }

  return mailApiPost<WarmBodiesResponse>("imap/warm-bodies.php", {
    accountId: params.accountId,
    folder: params.folder,
    uids: params.uids,
  });
}

export function fetchMailMessage(
  params: {
    uid: number;
    accountId?: number;
    folder?: string;
    refresh?: boolean;
    /** Lecture DD02 uniquement (polling worker). */
    check?: boolean;
  },
  options?: { timeoutMs?: number }
) {
  const qs = new URLSearchParams();
  qs.set("uid", String(params.uid));
  if (params.accountId) qs.set("accountId", String(params.accountId));
  if (params.folder) qs.set("folder", params.folder);
  if (params.refresh) qs.set("refresh", "1");
  if (params.check) qs.set("check", "1");

  return mailApiGet<MailMessageResponse>(`imap/message.php?${qs.toString()}`, options);
}

export function markMailMessageSeen(params: {
  uid?: number;
  uids?: number[];
  accountId?: number;
  folder?: string;
}) {
  const uids =
    params.uids && params.uids.length > 0
      ? params.uids
      : params.uid != null && params.uid > 0
        ? [params.uid]
        : [];

  return mailApiPost<{ success: boolean; uid: number; uids?: number[]; seen: boolean }>(
    "imap/mark-seen.php",
    {
      uids,
      accountId: params.accountId,
      folder: params.folder ?? "INBOX",
    }
  );
}

export function markMailMessageUnread(params: {
  uid?: number;
  uids?: number[];
  accountId?: number;
  folder?: string;
}) {
  const uids =
    params.uids && params.uids.length > 0
      ? params.uids
      : params.uid != null && params.uid > 0
        ? [params.uid]
        : [];

  return mailApiPost<{ success: boolean; uid: number; uids?: number[]; seen: boolean }>(
    "imap/mark-unread.php",
    {
      uids,
      accountId: params.accountId,
      folder: params.folder ?? "INBOX",
    }
  );
}

export type MailMessageFlagsResponse = {
  success: boolean;
  accountId: number;
  folder: string;
  flags: Array<{ uid: number; hasAttachments: boolean }>;
};

export function fetchMailMessageAttachmentFlags(params: {
  accountId?: number;
  folder?: string;
  uids: number[];
}) {
  if (params.uids.length === 0) {
    return Promise.resolve({
      ok: true as const,
      data: {
        success: true,
        accountId: params.accountId ?? 0,
        folder: params.folder ?? "INBOX",
        flags: [],
      },
    });
  }

  const qs = new URLSearchParams();
  qs.set("uids", params.uids.join(","));
  if (params.accountId) qs.set("accountId", String(params.accountId));
  if (params.folder) qs.set("folder", params.folder);

  return mailApiGet<MailMessageFlagsResponse>(`imap/message-flags.php?${qs.toString()}`);
}

export function createMailFolder(params: {
  accountId?: number;
  name?: string;
  parentFolder?: string | null;
  folder?: string;
}) {
  return mailApiPost<{ success: boolean; folder: import("@/lib/types/mail").MailFolder; message: string }>(
    "imap/create-folder.php",
    params
  );
}

export function renameMailFolder(params: {
  accountId?: number;
  folder: string;
  name: string;
}) {
  return mailApiPost<{
    success: boolean;
    folder: import("@/lib/types/mail").MailFolder;
    oldName: string;
    message: string;
  }>("imap/rename-folder.php", params);
}

export function deleteMailFolder(params: { accountId?: number; folder: string }) {
  return mailApiPost<{
    success: boolean;
    folderName: string;
    message: string;
  }>("imap/delete-folder.php", params);
}

export function fetchMailFolders(params?: {
  accountId?: number;
  refresh?: boolean;
  badges?: boolean;
}) {
  const qs = new URLSearchParams();
  if (params?.accountId) qs.set("accountId", String(params.accountId));
  if (params?.refresh) qs.set("refresh", "1");
  if (params?.badges) qs.set("badges", "1");
  const query = qs.toString();
  const path = query ? `imap/folders.php?${query}` : "imap/folders.php";
  return mailApiGet<MailFoldersResponse>(path, { timeoutMs: MAIL_FOLDERS_FETCH_TIMEOUT_MS });
}

export function buildMailAttachmentUrl(params: {
  uid: number;
  part: string;
  accountId?: number;
  folder?: string;
  filename?: string;
  inline?: boolean;
}): string {
  const qs = new URLSearchParams();
  qs.set("uid", String(params.uid));
  qs.set("part", params.part);
  if (params.accountId) qs.set("accountId", String(params.accountId));
  if (params.folder) qs.set("folder", params.folder);
  if (params.filename) qs.set("filename", params.filename);
  if (params.inline) qs.set("inline", "1");
  const suiteUserId = readStoredSuiteUserId();
  if (suiteUserId) qs.set("suite_user_id", suiteUserId);
  return `${getMailApiBase()}/imap/attachment.php?${qs.toString()}`;
}

export async function fetchMailAttachmentBlob(params: {
  uid: number;
  part: string;
  accountId?: number;
  folder?: string;
  filename?: string;
}): Promise<Blob> {
  return withImapExclusiveOp(async () => {
    const url = buildMailAttachmentUrl({ ...params, inline: true });
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 90_000);
    try {
      const res = await fetch(url, {
        credentials: "include",
        headers: mailAuthHeaders(),
        signal: controller.signal,
      });
      if (!res.ok) {
        let message = `Erreur HTTP ${res.status}`;
        try {
          const body = (await res.json()) as { message?: string };
          if (body.message) message = body.message;
        } catch {
          /* binaire */
        }
        throw new Error(message);
      }
      return res.blob();
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") {
        throw new Error("Délai dépassé — le serveur met trop de temps à envoyer la pièce jointe.");
      }
      throw err;
    } finally {
      window.clearTimeout(timer);
    }
  });
}

export async function downloadMailAttachment(params: {
  uid: number;
  part: string;
  accountId?: number;
  folder?: string;
  filename: string;
}): Promise<void> {
  const url = buildMailAttachmentUrl(params);
  const res = await fetch(url, {
    credentials: "include",
    headers: mailAuthHeaders(),
  });
  if (!res.ok) {
    let message = `Erreur HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { message?: string };
      if (body.message) message = body.message;
    } catch {
      /* binaire */
    }
    throw new Error(message);
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = params.filename || "piece-jointe";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
}
